import React, { useState, useEffect, useMemo } from 'react';
import { unitService, Unit } from '../services/units';
import { Pagination } from '../components/Pagination';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Loader2, Box, Edit, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { useForm } from 'react-hook-form';

interface UnitFormData {
  name: string;
  abbreviation: string;
}

export const UnitsPage: React.FC = () => {
  const [allUnits, setAllUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Client-side pagination
  const totalElements = allUnits.length;
  const totalPages = Math.ceil(totalElements / pageSize);
  const units = useMemo(() => {
    const start = currentPage * pageSize;
    return allUnits.slice(start, start + pageSize);
  }, [allUnits, currentPage, pageSize]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UnitFormData>();

  useEffect(() => {
    loadUnits();
  }, []);

  const loadUnits = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await unitService.getAllUnits();
      setAllUnits(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load units');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (unit?: Unit) => {
    if (unit) {
      setEditingUnit(unit);
      reset({
        name: unit.name,
        abbreviation: unit.abbreviation,
      });
    } else {
      setEditingUnit(null);
      reset({});
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUnit(null);
    reset({});
  };

  const onSubmit = async (data: UnitFormData) => {
    try {
      if (editingUnit) {
        await unitService.updateUnit(editingUnit.id, data);
      } else {
        await unitService.createUnit(data);
      }
      await loadUnits();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || 'Failed to save unit');
    }
  };

  const handleToggleActive = async (id: number, currentIsActive: boolean) => {
    try {
      if (currentIsActive) {
        await unitService.deactivateUnit(id);
      } else {
        await unitService.activateUnit(id);
      }
      await loadUnits();
    } catch (err: any) {
      setError(err.message || 'Failed to toggle unit status');
    }
  };

  const handleDelete = async (id: number) => {
    if (
      window.confirm('Are you sure you want to delete this unit? This action cannot be undone.')
    ) {
      try {
        await unitService.deleteUnit(id);
        await loadUnits();
      } catch (err: any) {
        setError(err.message || 'Failed to delete unit');
      }
    }
  };

  if (loading && units.length === 0) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Units</h1>
          <p className="text-muted-foreground">Manage units for operations and job listings</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Unit
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All Units</CardTitle>
          <CardDescription>List of all units available for operations</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Abbreviation</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {units.map((unit) => (
                <TableRow key={unit.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Box className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{unit.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{unit.abbreviation}</TableCell>
                  <TableCell>
                    <Badge variant={unit.isActive ? 'default' : 'secondary'}>
                      {unit.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(unit.id, unit.isActive)}
                      >
                        {unit.isActive ? (
                          <ToggleRight className="h-4 w-4" />
                        ) : (
                          <ToggleLeft className="h-4 w-4" />
                        )}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(unit)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(unit.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {units.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">
              No units found. Create your first unit to get started.
            </p>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(0);
            }}
            itemName="units"
          />
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingUnit ? 'Edit Unit' : 'Create Unit'}</DialogTitle>
            <DialogDescription>
              {editingUnit ? 'Update unit details' : 'Add a new unit'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="name">Unit Name *</Label>
              <Input
                id="name"
                {...register('name', { required: true })}
                placeholder="e.g., Hours, Days, Pieces"
              />
              {errors.name && <p className="text-sm text-destructive">Name is required</p>}
            </div>

            <div>
              <Label htmlFor="abbreviation">Abbreviation *</Label>
              <Input
                id="abbreviation"
                {...register('abbreviation', { required: true })}
                placeholder="e.g., hr, day, pcs"
              />
              {errors.abbreviation && (
                <p className="text-sm text-destructive">Abbreviation is required</p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit">{editingUnit ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnitsPage;
